import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ConfirmDeleteModal from '../components/shared/ConfirmDeleteModal';
import { RequestDetailShell, RequestHeader } from '../components/workflow/RequestDetailShell';
import RecordActionsCard from '../components/workflow/RecordActionsCard';
import { SectionCard, DetailGrid, DetailItem } from '../components/workflow/SectionCard';
import AttachmentsList from '../components/workflow/AttachmentsList';
import WorkflowRefLink from '../components/shared/WorkflowRefLink';
import HistoryCard from '../components/workflow/HistoryCard';
import EngagementRecordFormModal from '../components/records/EngagementRecordFormModal';
import { recordKind } from '../components/records/recordKinds';
import { SignedFormPendingNotice, FormPdfDownload } from '../components/records/legacyImport';
import { ProvenanceBadge, provenanceOrigin } from '../components/shared/provenance';
import { CoveredEngagementsPanel, fmtCoverageDefault } from '../components/workflow/CoverageDisplay';
import { qc35AnswerLabels, expandQc35AnswerChanges } from './qc35Questions';
import { fetchJSON, API_BASE_URL } from '../utils/api';

// Management — 一筆 CW/CC ・ Signed Financial Statements ・ QC35 正式記錄的詳情
// （docs/modules/engagement-records.md）。版型與 workflow 的審核 Detail 共用同
// 一組元件，差別只在這裡沒有審核動作——記錄不是申請
// 單，它已經是核准的產物。
//
// 編輯走 `EngagementRecordFormModal`，與清單頁的「New record」是同一支表單
// （QC6/QC7 記錄頁已驗證的形狀）。**建立與編輯的欄位不會漂移**,因為根本只有
// 一份。
//
// `/qm/records/:kind/new` 也走這裡:沒有底層記錄,直接開空白表單,存檔後導到新
// 記錄自己的網址。
//
// 編輯**會回寫 engagement 主檔**：CW/CC 寫 caseware_opened
// 與 charge_code_assigned、FS 蓋 sign_off_date 與 archival_deadline、QC35 把涵蓋案標記
// 完成。記錄可以編輯卻不回寫，只會讓記錄與逾期報表各說各話。
const QMEngagementRecordDetail = () => {
  const { kind, id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const cfg = recordKind(kind);
  const isNew = id === 'new';
  const canEdit = hasPermission('engagements:edit');

  const [record, setRecord] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    if (isNew) return;
    try {
      const [detail, hist] = await Promise.all([
        fetchJSON(`/api/qm/engagement-records/${kind}/${id}`),
        fetchJSON(`/api/qm/engagement-records/${kind}/${id}/events`),
      ]);
      setRecord(detail.record);
      setEvents(hist.events || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [kind, id, isNew]);

  useEffect(() => { if (cfg) load(); }, [cfg, load]);

  if (!cfg) return <Navigate to="/manage" replace />;

  // 新增模式：沒有底層內容，直接開表單。
  if (isNew) {
    return (
      <EngagementRecordFormModal
        kind={kind}
        record={null}
        onClose={() => navigate(cfg.listTo)}
        onSaved={(saved) => navigate(saved && saved.id ? `${cfg.listTo}/${saved.id}` : cfg.listTo)}
      />
    );
  }

  return (
    <RequestDetailShell
      backTo={cfg.listTo}
      backLabel={`${cfg.label} Records`}
      loading={!record}
      error={error}
      notice={notice}
    >
      {record && (
        <>
          <RequestHeader
            requestNo={record.sourceRequestNo || `#${record.id}`}
            typeLabel={`${cfg.label} Record`}
            originLabel={record.sourceRequestNo
              ? <>produced by <WorkflowRefLink refNo={record.sourceRequestNo} className="text-sm" /></>
              : provenanceOrigin(record.source) || 'produced by an approval'}
            submittedAt={record.createdAt}
            badge={<ProvenanceBadge source={record.source} />}
          />

          <SignedFormPendingNotice pending={record.signedFormPending === true} />

          {canEdit && (
            <RecordActionsCard
              onEdit={() => setEditing(true)}
              onDelete={() => setDeleting(true)}
            />
          )}

          <SectionCard
            title="Record"
            actions={kind === 'qc35' && (record.answers || record.legacyImport) ? (
              // Form QC35 的可列印版（含核准欄）——與 QC6/7 記錄的表單下載同一模式。
              <FormPdfDownload
                record={record}
                formName="QC35"
                signedDocType="qc35"
                href={`${API_BASE_URL}/api/qm/engagement-records/qc35/${record.id}/pdf`}
                signedHref={(f) => `${API_BASE_URL}/api/qm/engagement-records/qc35/${record.id}/files/${f.id}`}
                className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
              >
                Download Form QC35 (PDF)
              </FormPdfDownload>
            ) : null}
          >
            <DetailGrid>
              {cfg.rows(record).map(([label, value]) => (
                <DetailItem key={label} label={label}>{value}</DetailItem>
              ))}
              <DetailItem label="Remarks">{record.remarks || '—'}</DetailItem>
            </DetailGrid>
          </SectionCard>

          {record.engagements && (
            <SectionCard title={`Engagements Covered (${record.engagements.length})`}>
              <CoveredEngagementsPanel items={record.engagements} />
            </SectionCard>
          )}

          <SectionCard title="Documents">
            {/* 被取代的版本留著並標示（契約 §3.2：正式資料只加版本、不硬刪）。 */}
            <AttachmentsList
              files={(record.files || []).map((f) => ({
                ...f,
                originalName: f.supersededAt
                  ? `${f.originalName} · previous version${f.supersededByRef ? ` · replaced by ${f.supersededByRef}` : ''}`
                  : f.originalName,
              }))}
              downloadHref={(f) => `${API_BASE_URL}/api/qm/engagement-records/${kind}/${record.id}/files/${f.id}`}
            />
          </SectionCard>

          <HistoryCard
            events={events}
            changeLabels={{
              // 這頁同時服務 fs 與 qc35 — 兩種 kind 的 diff 鍵都要有對照，
              // 標籤跟著 recordKinds 的閱讀列走。
              engagements: 'Engagements Covered',
              signingDate: 'Signing Date',
              auditOpinion: 'Audit Opinion',
              techToolsUsed: 'Technological Tools Used',
              techToolsAreas: 'Tool Deployment Areas',
              restatement: 'Restatement',
              restatementDesc: 'Restatement Description',
              archivalDate: 'Archival Date',
              answers: 'Form QC35 Checklist',
              legacyImport: 'Imported from Signed Form',
              remarks: 'Remarks',
              // QC35 checklist 的題目名（answers.<id> 逐題列）。
              ...qc35AnswerLabels(),
            }}
            fmtChangeValue={fmtCoverageDefault}
            expandChanges={kind === 'qc35' ? expandQc35AnswerChanges : undefined}
          />

          {editing && (
            <EngagementRecordFormModal
              kind={kind}
              record={record}
              onClose={() => setEditing(false)}
              onSaved={(saved, applied) => {
                setEditing(false);
                setNotice(describeApplied(applied) || 'Record updated.');
                load();
              }}
            />
          )}
          {deleting && (
            <ConfirmDeleteModal
              title={`Delete This ${cfg.label} Record`}
              message={'This permanently deletes the record and any documents uploaded with it here. '
                + 'The originating request is NOT touched, the engagement keeps the values already written to it, '
                + 'and the history row survives. This cannot be undone.'}
              onClose={() => setDeleting(false)}
              onConfirm={async () => {
                await fetchJSON(`/api/qm/engagement-records/${kind}/${record.id}`, { method: 'DELETE' });
                navigate(cfg.listTo);
              }}
            />
          )}
        </>
      )}
    </RequestDetailShell>
  );
};

/**
 * 回寫結果一句話。**失敗一定要說**——回寫就是使用者按下去要的那件事，靜默
 * 失敗會讓人以為主檔跟著改了，而逾期報表其實沒動。
 */
export const describeApplied = (applied) => {
  if (!applied) return null;
  const failed = (applied.failed || []).length;
  const written = (applied.written || []).length;
  const completed = (applied.completed || []).length;
  if (failed > 0) {
    return `Record saved, but writing back to ${failed} engagement(s) failed — check the engagement and retry.`;
  }
  if (applied.skipped) return `Record saved. ${applied.skipped}`;
  if (completed > 0) return `Record saved and ${completed} engagement(s) marked completed.`;
  if (written > 0) return `Record saved and written back to ${written} engagement(s).`;
  return 'Record saved.';
};

export default QMEngagementRecordDetail;
